import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateFlussonicServerRequest,
  FlussonicServer,
  flussonicServerService,
  UpdateFlussonicServerRequest,
} from '@/services/flussonicServerService';

export const flussonicServerKeys = {
  all: ['flussonicServers'] as const,
  list: () => [...flussonicServerKeys.all, 'list'] as const,
  details: (id: string) => [...flussonicServerKeys.all, 'details', id] as const,
  streams: (id: string) => [...flussonicServerKeys.all, 'streams', id] as const,
};

export const useFlussonicServers = () => {
  return useQuery({
    queryKey: flussonicServerKeys.list(),
    queryFn: flussonicServerService.getServers,
    select: (data) => data.servers,
  });
};

export const useFlussonicServerDetails = (id: string | undefined) => {
  return useQuery({
    queryKey: flussonicServerKeys.details(id ?? 'unknown'),
    queryFn: () => {
      if (!id) {
        throw new Error('Servidor no encontrado');
      }

      return flussonicServerService.getServerDetails(id);
    },
    enabled: Boolean(id),
  });
};

export const useFlussonicServerStreams = (id: string | undefined) => {
  return useQuery({
    queryKey: flussonicServerKeys.streams(id ?? 'unknown'),
    queryFn: () => {
      if (!id) {
        throw new Error('Servidor no encontrado');
      }

      return flussonicServerService.getServerStreams(id);
    },
    enabled: Boolean(id),
    select: (data) => data.streams,
  });
};

export const useCreateFlussonicServer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateFlussonicServerRequest) => flussonicServerService.createServer(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: flussonicServerKeys.all });
    },
  });
};

export const useUpdateFlussonicServer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateFlussonicServerRequest) => flussonicServerService.updateServer(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: flussonicServerKeys.all });
    },
  });
};

export const useDeleteFlussonicServer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => flussonicServerService.deleteServer(id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: flussonicServerKeys.list() });
      const previousServers = queryClient.getQueryData<FlussonicServer[]>(flussonicServerKeys.list());
      if (previousServers) {
        queryClient.setQueryData(
          flussonicServerKeys.list(),
          previousServers.filter((server) => server.id !== id)
        );
      }
      return { previousServers };
    },
    onError: (_error, _id, context) => {
      if (context?.previousServers) {
        queryClient.setQueryData(flussonicServerKeys.list(), context.previousServers);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: flussonicServerKeys.all });
    },
  });
};
